import React, { useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical, X, Copy, Loader2, Loader } from "lucide-react";
import Dropdown from "../../../components/ui/Dropdown";
import Pagination from "../../../components/ui/Pagination";
import {
  useApproveBusinessDeposit,
  useDeclineBusinessDeposit,
  useBusinessDeposit,
} from "../hooks/useBusinessDeposit";
import { toast } from "react-hot-toast";

// details modal
const DetailsModal = ({ isOpen, onClose, data, onApprove, onReject }) => {
  if (!isOpen || !data) return null;

  const detailItem = (label, value) => (
    <div className="flex flex-col mb-4">
      <span className="text-xs tracking-wider text-gray-500 uppercase">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value || "N/A"}</span>
    </div>
  );

  const formatNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? "0.00" : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  const symbol = data.currency?.symbol || data.wallet?.currency?.symbol || "";

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 transition-opacity">
      <div className="overflow-hidden h-[90vh] mx-4 w-full max-w-2xl bg-white rounded-2xl shadow-2xl transition-all duration-200 transform animate-in fade-in zoom-in">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Business Deposit Details</h3>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 max-h-[80%] overflow-y-auto">
          <div className="grid grid-cols-1 gap-y-2 gap-x-8 md:grid-cols-2">
            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Transaction Information</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {detailItem("Transaction ID", data.uuid)}
                {detailItem("Reference", data.reference)}
                {detailItem("Gateway Reference", data.paymentGatewayReference)}
                {detailItem("Status", data.status?.replace("_", " ").toUpperCase())}
                {detailItem("Date", data.createdAt ? new Date(data.createdAt).toLocaleString() : "N/A")}
                {detailItem("Amount", `${symbol} ${formatNumber(data.amount)}`)}
                {detailItem("Total Amount", `${symbol} ${formatNumber(data.totalAmount)}`)}
                {detailItem("Platform Fee", `${symbol} ${formatNumber(data.platformFee)}`)}
                {detailItem("Provider Fee", `${symbol} ${formatNumber(data.paymentGatewayFee)}`)}
              </div>
            </div>

            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Business Details</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {detailItem("Business Name", data.wallet?.business?.name)}
                {detailItem("Business Email", data.wallet?.business?.email)}
              </div>
            </div>

            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Currency Info</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {detailItem("Code", data.wallet?.currency?.currencyCode)}
                {detailItem("Symbol", data.wallet?.currency?.symbol)}
                {detailItem("Country", data.wallet?.currency?.country)}
              </div>
            </div>

            <div className="col-span-2 mt-1">
              <span className="block pb-2 mb-2 text-xs font-bold text-emerald-800 uppercase border-b border-emerald-100">Description</span>
              <p className="font-sans text-sm italic leading-relaxed text-gray-700">
                {data.description || "No description provided"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center items-center px-2 py-6 space-x-2 space-y-0 bg-gray-50 border-t md:justify-end md:space-y-0 md:space-x-4">
          <button
            onClick={() => onReject(data)}
            className="p-2 font-bold text-red-600 bg-white rounded-xl border-2 border-red-500 shadow-sm transition-all md:py-3 md:px-8 hover:bg-red-50 active:scale-95"
          >
            Decline Deposit
          </button>

          <button
            onClick={() => {
              onApprove(data);
              onClose();
            }}
            className="flex justify-center items-center p-2 font-bold text-white bg-emerald-600 rounded-xl shadow-lg transition-all md:py-3 md:px-8 hover:bg-emerald-700 active:scale-95"
          >
            Approve Deposit
          </button>
        </div>
      </div>
    </div>
  );
};

// Business-specific decline modal (mirrors the shared DeclineReasonModal,
// but shows the business rather than a personal user, since that shared
// component reads data.user which doesn't exist on a business row).
const DeclineBusinessReasonModal = ({ isOpen, onClose, onSubmit, isLoading, data }) => {
  const [reason, setReason] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reason.trim()) {
      onSubmit(reason);
      setReason("");
    }
  };

  const symbol = data?.currency?.symbol || data?.wallet?.currency?.symbol || "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] w-full p-4 overflow-hidden">
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transition-all duration-200 transform animate-in fade-in zoom-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 transition-colors hover:text-gray-600"
        >
          <X size={20} />
        </button>

        <div className="space-y-6">
          <div className="text-center">
            <h3 className="mb-2 font-sans text-xl font-bold text-gray-900">Decline Business Deposit</h3>

            <p className="font-sans text-sm text-gray-500">
              Please provide a reason for declining this request. This will be recorded on the transaction.
            </p>

            <div className="p-1 mt-3 bg-gray-100 rounded-lg">
              <p className="font-sans text-sm text-gray-500">
                Reference: <span className="font-semibold">{data?.reference}</span>
              </p>
              <p className="font-sans text-sm text-gray-500">
                Gateway Ref: <span className="font-semibold">{data?.paymentGatewayReference || ""}</span>
              </p>
              <div className="flex gap-3 justify-center items-center">
                <p className="font-sans text-sm text-gray-500">
                  Business: <span className="font-semibold">{data?.wallet?.business?.name}</span>
                </p>
                <p className="font-sans text-sm text-gray-500">
                  Amount: <span className="font-semibold">{symbol}{Number(data?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col">
              <label className="block mb-2 font-sans text-sm font-semibold text-gray-700">
                Feedback/Reason
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter decline reason here..."
                required
                rows={4}
                className="p-3 w-full font-sans text-base rounded-xl border border-gray-200 transition-all outline-none resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 font-sans font-bold text-gray-700 rounded-xl border border-gray-200 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !reason.trim()}
                className="flex-1 py-3 px-4 bg-red-600 text-white rounded-xl font-bold font-sans hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Declining...
                  </>
                ) : (
                  "Confirm Decline"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Simple confirm modal - the business approve endpoint takes no body
// (no reserve/OTC hold options like the personal one), so there is no
// form to fill in here, just a confirmation.
const ApproveConfirmModal = ({ isOpen, onClose, onConfirm, isLoading, data }) => {
  if (!isOpen || !data) return null;

  const symbol = data.currency?.symbol || data.wallet?.currency?.symbol || "";

  return (
    <div className="flex fixed inset-0 z-[100] justify-center items-center p-4 w-full bg-black bg-opacity-50 overflow-hidden">
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transition-all duration-200 transform animate-in fade-in zoom-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 transition-colors hover:text-gray-600"
        >
          <X size={20} />
        </button>

        <div className="space-y-6 text-center">
          <h3 className="mb-2 font-sans text-xl font-bold text-gray-900">Approve Deposit</h3>
          <p className="font-sans text-sm text-gray-500">
            This will credit the business wallet for the full amount below. This action cannot be undone.
          </p>

          <div className="p-3 bg-gray-100 rounded-lg">
            <p className="font-sans text-sm text-gray-500">
              Business: <span className="font-semibold">{data.wallet?.business?.name}</span>
            </p>
            <p className="font-sans text-sm text-gray-500">
              Amount: <span className="font-semibold">{symbol}{Number(data.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </p>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 font-sans font-bold text-gray-700 rounded-xl border border-gray-200 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="flex flex-1 justify-center items-center px-4 py-3 font-sans font-bold text-white bg-emerald-600 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-700"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                "Confirm Approve"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function BusinessDepositTable() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);

  const [loadingRowId, setLoadingRowId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(globalFilter);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [globalFilter]);

  const { data, isLoading, isError, error } = useBusinessDeposit(page, limit, debouncedFilter);
  const { mutate: approveDeposit, isPending: isApproving } = useApproveBusinessDeposit();
  const { mutate: declineDeposit, isPending: isDeclining } = useDeclineBusinessDeposit();

  const handleViewDetails = (deposit) => {
    setSelectedDeposit(deposit);
    setIsDetailsModalOpen(true);
  };

  const handleOpenDecline = (deposit) => {
    setDeclineTarget(deposit);
    setIsDeclineModalOpen(true);
  };

  const handleOpenApprove = (deposit) => {
    setApproveTarget(deposit);
    setIsApproveModalOpen(true);
  };

  const handleConfirmDecline = (reason) => {
    if (!declineTarget) return;

    setLoadingRowId(declineTarget.uuid);
    declineDeposit(
      {
        transactionUuid: declineTarget?.uuid,
        reason: reason,
      },
      {
        onSuccess: () => {
          toast.success("Business deposit declined successfully");
          setIsDeclineModalOpen(false);
          setIsDetailsModalOpen(false);
          setDeclineTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to decline deposit");
          setLoadingRowId(null);
        },
      }
    );
  };

  const handleConfirmApprove = () => {
    if (!approveTarget) return;

    setLoadingRowId(approveTarget.uuid);
    approveDeposit(approveTarget.uuid, {
      onSuccess: () => {
        toast.success("Business deposit approved successfully");
        setIsApproveModalOpen(false);
        setIsDetailsModalOpen(false);
        setApproveTarget(null);
        setLoadingRowId(null);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to approve deposit");
        setLoadingRowId(null);
      },
    });
  };

  const truncateText = (text, length = 10) => {
    if (!text) return "";
    return text.length > length
      ? `${text.slice(0, length)}...`
      : text;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const columns = [
    {
      accessorKey: "uuid",
      header: "Transaction ID",
      cell: ({ getValue }) => {
        const value = getValue();

        return (
          <div className="flex gap-1 justify-between items-center min-w-[130px]">
            <span title={value}>
              {truncateText(value, 12)}
            </span>

            <button
              onClick={() => handleCopy(value)}
              className="text-gray-500 hover:text-black"
            >
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "reference",
      header: "Reference",
      cell: ({ row }) => {
        const value = row.original.reference;

        return (
          <div className="flex gap-2 justify-between items-center min-w-[130px]">
            <span title={value}>
              {truncateText(value || "--", 12)}
            </span>

            <button
              onClick={() => handleCopy(value)}
              className="text-gray-500 hover:text-black"
            >
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "paymentGatewayReference",
      header: "Gateway Reference",
      cell: ({ row }) => {
        const value = row.original.paymentGatewayReference;

        return (
          <div className="flex gap-2 justify-between items-center min-w-[150px]">
            <span title={value}>
              {truncateText(value, 15)}
            </span>

            <button
              onClick={() => handleCopy(value)}
              className="text-gray-500 hover:text-black"
            >
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Date",
      cell: (info) => {
        const val = info.row.original.createdAt;
        return val ? new Date(val).toLocaleString() : "N/A";
      }
    },
    {
      id: "business",
      header: "Business",
      accessorFn: (row) => row.wallet?.business?.name || "",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">
            {row.original.wallet?.business?.name || "N/A"}
          </span>
          <span className="text-xs text-gray-500">{row.original.wallet?.business?.email}</span>
        </div>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      accessorFn: (row) => {
        const symbol = row.wallet?.currency?.symbol || "";
        return `${symbol}${row.amount}`;
      },
      cell: ({ row }) => (
        <span className="font-semibold whitespace-nowrap">
          {row.original.wallet?.currency?.symbol || ""}{" "}
          {Number(row.original.amount || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      id: "totalAmount",
      header: "Total Amount",
      accessorFn: (row) => {
        const symbol = row.wallet?.currency?.symbol || "";
        return `${symbol}${row.totalAmount}`;
      },
      cell: ({ row }) => (
        <span className="text-gray-600 whitespace-nowrap">
          {row.original.wallet?.currency?.symbol || ""} {Number(row.original.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      id: "serviceType",
      accessorKey: "serviceType",
      header: "Service Type",
      cell: (info) => (
        <span className="capitalize">{info.getValue()?.toLowerCase().replace("_", " ")}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();

        // Business/merchant statuses (BUSINESS_PAYMENT_STATUS) are
        // upper-case, unlike the personal side's lower-case PaymentStatus.
        const statusStyles = {
          MANUAL_REVIEW: "bg-yellow-100 text-yellow-800",
          SUCCESS: "bg-green-100 text-green-800",
          FAILED: "bg-red-100 text-red-800",
          PENDING: "bg-blue-100 text-blue-800",
          CLOSE: "bg-gray-200 text-gray-700",
        };

        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusStyles[status] || "bg-gray-100 text-gray-800"
              }`}
          >
            {status?.replace("_", " ")}
          </span>
        );
      }
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const deposit = row.original;
        const isRowLoading = loadingRowId === deposit.uuid;

        const dropdownItems = [
          {
            label: "View Details",
            onClick: () => handleViewDetails(deposit),
          },
          {
            label: "Approve",
            onClick: () => handleOpenApprove(deposit),
            className: "text-green-600",
          },
          {
            label: "Decline",
            onClick: () => handleOpenDecline(deposit),
            className: "text-red-600",
          },
        ];

        return (
          <Dropdown
            trigger={isRowLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
            items={dropdownItems}
          />
        );
      },
    },
  ];

  // The API paginates and searches server-side — render its page as-is.
  const records = React.useMemo(() => data?.data ?? [], [data]);
  const totalPages = data?.pagination?.totalPages || 1;
  const totalItems = data?.pagination?.total || 0;
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, totalItems);

  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    initialState: {
      pagination: {
        pageSize: limit,
        pageIndex: page - 1,
      },
    },
  });

  const handleSearch = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (isError) {
    return (
      <div className="flex h-screen">
        <div className="flex flex-1 justify-center items-center">
          <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
            Error: {error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex justify-between items-center pb-5 bg-white">
        <div className="relative w-80">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by ref or business name/email..."
            className="py-3 pr-4 pl-12 w-full text-sm rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={globalFilter}
            onChange={handleSearch}
          />
        </div>
      </div>

      {
        isLoading ? (
          <div className="flex h-[400px]">
            <div className="flex flex-1 justify-center items-center">
              <p className="flex gap-2 items-center font-medium text-gray-600">Loading business deposits review data <Loader className="w-5 h-5 animate-spin" /></p>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded border border-gray-200">
            <div className="overflow-x-auto min-h-[400px]">
              <table className="w-full">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-gray-50/50">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6 ${header.column.columnDef.id === "serviceType" || header.column.columnDef.id === "totalAmount" ? "min-w-[140px]" : ""}`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-100 hover:border-b">
                  {records.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="transition-colors hover:bg-gray-50">
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center">
                          <p className="text-lg font-medium">No records matching your search</p>
                          <p className="text-sm">Try adjusting your filters or search terms</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-4 justify-between items-center p-3 border-t border-gray-200 md:p-6 bg-gray-50/50 md:flex-row">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{rangeStart}</span> to{" "}
                <span className="font-medium">{rangeEnd}</span>{" "}
                of <span className="font-medium">{totalItems}</span> entries
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )
      }

      <DetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        data={selectedDeposit}
        onApprove={handleOpenApprove}
        onReject={handleOpenDecline}
      />

      <DeclineBusinessReasonModal
        isOpen={isDeclineModalOpen}
        onClose={() => setIsDeclineModalOpen(false)}
        onSubmit={handleConfirmDecline}
        isLoading={isDeclining}
        data={declineTarget}
      />

      <ApproveConfirmModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onConfirm={handleConfirmApprove}
        isLoading={isApproving}
        data={approveTarget}
      />
    </div>
  );
}

export default BusinessDepositTable;
